/**
 * Brand-color social media icons.
 *
 * Lucide ships generic Instagram / Youtube glyphs but no TikTok, no
 * Pinterest, and an outdated bird-era Twitter glyph. We render
 * inline SVG for parity so every channel rail looks like the same
 * visual family — coloured circle, white glyph.
 *
 * Paths are simplified/uniform-stroke versions of each brand's
 * official mark so they read cleanly at 24-32px. Brand colours
 * are taken from each platform's public guidelines.
 */
import type { ChannelService } from "../api";

const BRAND_BG: Record<ChannelService, string> = {
  instagram: "linear-gradient(135deg,#FEDA77 0%,#F58529 25%,#DD2A7B 50%,#8134AF 75%,#515BD4 100%)",
  youtube: "#FF0033",
  tiktok: "#000000",
  twitter: "#000000",
  pinterest: "#E60023",
  facebook: "#1877F2",
  linkedin: "#0A66C2",
  threads: "#000000",
  mastodon: "#6364FF",
  bluesky: "#1185FE",
  unknown: "rgba(255,255,255,.08)",
};

/** Per-service halo colour for the selected-state glow. Solid, readable
 * hues; the black-brand marks (X, TikTok, Threads) get a neutral grey so
 * the glow reads instead of vanishing into the dark theme. */
const BRAND_GLOW: Record<ChannelService, string> = {
  instagram: "#DD2A7B",
  youtube: "#FF0033",
  tiktok: "#8A8F98",
  twitter: "#8A8F98",
  pinterest: "#E60023",
  facebook: "#1877F2",
  linkedin: "#0A66C2",
  threads: "#8A8F98",
  mastodon: "#6364FF",
  bluesky: "#1185FE",
  unknown: "#8A8F98",
};

const BRAND_LABEL: Record<ChannelService, string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  twitter: "X",
  pinterest: "Pinterest",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  threads: "Threads",
  mastodon: "Mastodon",
  bluesky: "Bluesky",
  unknown: "Other",
};

/** Inline brand-mark glyphs, white-on-bg. Each path is centred in a
 * 24×24 viewBox and tuned for ~14px draw size inside a 28-32px circle. */
function Glyph({ service }: { service: ChannelService }): React.JSX.Element {
  const stroke = "currentColor";
  const fill = "currentColor";
  switch (service) {
    case "instagram":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M7 2.5h10A4.5 4.5 0 0 1 21.5 7v10a4.5 4.5 0 0 1-4.5 4.5H7A4.5 4.5 0 0 1 2.5 17V7A4.5 4.5 0 0 1 7 2.5Zm5 4.75A4.75 4.75 0 1 0 16.75 12 4.75 4.75 0 0 0 12 7.25Zm0 1.5A3.25 3.25 0 1 1 8.75 12 3.25 3.25 0 0 1 12 8.75Zm5.5-2.75a1 1 0 1 0 1 1 1 1 0 0 0-1-1Z"
            fill={fill}
          />
        </svg>
      );
    case "youtube":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M23 6.5s-.2-1.6-.9-2.3c-.8-.8-1.7-.8-2.1-.9C16.9 3 12 3 12 3s-4.9 0-8 .3c-.4 0-1.3.1-2.1.9-.7.7-.9 2.3-.9 2.3S.7 8.4.7 10.3v1.4c0 1.9.2 3.8.2 3.8s.2 1.6.9 2.3c.8.8 2 .8 2.4.9 1.7.2 7.7.3 7.7.3s4.9 0 8-.3c.4 0 1.3-.1 2.1-.9.7-.7.9-2.3.9-2.3s.2-1.9.2-3.8v-1.4c0-1.9-.2-3.8-.2-3.8ZM9.7 14.4V8.1l6 3.2-6 3.1Z" fill={fill} />
        </svg>
      );
    case "tiktok":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M17.5 5.5c1.3.9 2.9 1.5 4.5 1.5v3a8 8 0 0 1-4.5-1.4v6.1a6 6 0 1 1-6-6v3.1a2.9 2.9 0 1 0 2.9 2.9V2h3.1a4.5 4.5 0 0 0 4.5 3.5Z"
            fill={fill}
          />
        </svg>
      );
    case "twitter": // X
      return (
        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
          <path
            d="M18.2 2H21l-6.7 7.6L22 22h-6.3l-4.9-6.4L5 22H2.2l7.1-8.2L2 2h6.5l4.5 5.9L18.2 2Zm-1 18h1.7L7.1 4H5.3l11.9 16Z"
            fill={fill}
          />
        </svg>
      );
    case "pinterest":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M12 2.2A9.8 9.8 0 0 0 8.4 21c0-.8 0-1.7.2-2.5l1.4-5.8s-.4-.7-.4-1.7c0-1.6.9-2.8 2-2.8 1 0 1.5.7 1.5 1.6 0 1-.6 2.5-1 3.9-.2 1.2.6 2.1 1.7 2.1 2 0 3.6-2.2 3.6-5.3 0-2.8-2-4.8-4.8-4.8-3.3 0-5.2 2.5-5.2 5 0 1 .4 2 .9 2.6.1.1.1.2.1.3l-.3 1.3c0 .2-.2.3-.4.2-1.4-.7-2.3-2.7-2.3-4.5C5.4 7 7.8 4 12.6 4c3.8 0 6.8 2.7 6.8 6.4 0 3.8-2.4 6.9-5.7 6.9-1.1 0-2.2-.6-2.6-1.3l-.7 2.7c-.2.9-.9 2.1-1.4 2.8a9.9 9.9 0 1 0 3-19.3Z"
            fill={fill}
          />
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M13.5 22v-9h3l.5-3.5h-3.5V7.3c0-1 .3-1.7 1.7-1.7H17V2.4c-.3 0-1.5-.1-2.8-.1-2.8 0-4.7 1.7-4.7 4.8v2.6H6.5V13h3v9Z" fill={fill} />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M4.5 3.5A2 2 0 1 1 4.5 7.6 2 2 0 0 1 4.5 3.5ZM2.7 9h3.6V21H2.7Zm6.4 0h3.4v1.6h.1c.5-.9 1.7-1.8 3.5-1.8 3.7 0 4.4 2.4 4.4 5.5V21h-3.7v-5.4c0-1.3 0-2.9-1.8-2.9-1.7 0-2 1.4-2 2.8V21H9.1Z" fill={fill} />
        </svg>
      );
    case "threads":
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M12 2C6.5 2 2.5 5.4 2.5 11.8c0 6.5 4 10.2 9.5 10.2 2.8 0 5-.7 6.5-2.4 1.3-1.5 2-3.4 2-5.6 0-3.4-2.2-5.8-6.3-6.6.3-2.4-.7-3.7-2.4-3.7-1.3 0-2.3.7-2.7 2-1.1 0-2 .4-2.5 1.1L4.8 7.8c.7-1 2.1-1.8 4-1.8 2.7 0 4 1.6 4 3.6 0 .6-.1 1-.1 1.3 1.6.2 3.5.7 4.5 2.2.7 1.1.9 2.6.4 4.1-1 3-3.7 4.4-6.7 4.4-4.6 0-8-2.8-8-8.3 0-5.6 3.4-9.3 8.7-9.3 2.6 0 4.7.9 6.2 2.4l-1.5 1.6c-1.2-1.2-2.8-1.9-4.7-1.9Z"
            fill={fill}
          />
        </svg>
      );
    case "mastodon":
    case "bluesky":
    case "unknown":
    default:
      return (
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <circle cx="12" cy="12" r="5" stroke={stroke} strokeWidth="2" fill="none" />
        </svg>
      );
  }
}

/** Brand-coloured chip with the channel's glyph. Default is a 28px circle
 * (matches the Settings list; the modal uses 32px). Pass `radius` for a
 * squircle (the destination picker uses 11) and `glow` to add the
 * selected-state brand halo. */
export function ServiceIcon({
  service,
  size = 28,
  radius,
  glow = false,
}: {
  service: ChannelService;
  size?: number;
  /** Corner radius override. Omit for a circle. */
  radius?: number;
  /** When true, ring the chip in its brand colour (selected state). */
  glow?: boolean;
}): React.JSX.Element {
  const restShadow = "0 1px 2px rgba(0,0,0,.4), inset 0 0 0 .5px rgba(255,255,255,.08)";
  const glowColor = BRAND_GLOW[service];
  const glowShadow =
    `inset 0 0 0 .5px rgba(255,255,255,.14), 0 2px 6px rgba(0,0,0,.45), ` +
    `0 0 0 3px color-mix(in oklab, ${glowColor} 26%, transparent), ` +
    `0 6px 20px -6px color-mix(in oklab, ${glowColor} 55%, transparent)`;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius ?? "50%",
        background: BRAND_BG[service],
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        flexShrink: 0,
        boxShadow: glow ? glowShadow : restShadow,
        transition: "box-shadow 220ms cubic-bezier(.2,.6,.2,1)",
      }}
      title={BRAND_LABEL[service]}
      aria-label={BRAND_LABEL[service]}
    >
      <Glyph service={service} />
    </div>
  );
}

export function serviceLabel(s: ChannelService): string {
  return BRAND_LABEL[s];
}

export function serviceBrandBg(s: ChannelService): string {
  return BRAND_BG[s];
}
